import { useWindowSize } from 'src/shared/hooks/use-window-size'

import PublicProfileHeader from '../ui/header'

// Skeleton components
const SkeletonCircle = ({ size, className = '' }: { size: string; className?: string }) => (
  <div className={`animate-pulse rounded-full bg-gray-200 ${size} ${className}`} />
)

const SkeletonRectangle = ({
  width,
  height,
  className = '',
}: {
  width: string
  height: string
  className?: string
}) => <div className={`animate-pulse rounded bg-gray-200 ${width} ${height} ${className}`} />

const SkeletonText = ({ width, height, className = '' }: { width: string; height: string; className?: string }) => (
  <div className={`animate-pulse rounded bg-gray-200 ${width} ${height} ${className}`} />
)

// Mobile Header Skeleton
const MobileHeaderSkeleton = () => (
  <>
    {/* Fixed Bottom Buttons */}
    <div className="fixed bottom-4 z-[11] mx-auto mt-2 flex w-full flex-row items-center gap-3 px-5">
      <div className="flex h-[60px] w-full flex-row items-center justify-between gap-3 rounded-full bg-white p-2 shadow-lg">
        <SkeletonRectangle width="w-1/2" height="h-[40px]" className="rounded-full" />
        <SkeletonRectangle width="w-1/3" height="h-[40px]" className="rounded-full" />
      </div>
    </div>

    {/* Cover Photo */}
    <div className="flex w-full flex-col items-start gap-4 bg-white">
      <SkeletonRectangle width="w-full" height="h-[160px]" />

      {/* Profile Section */}
      <div className="mb-[-50px] flex w-full translate-y-[-50px] flex-col gap-2 rounded-[40px] bg-white">
        <div className="mb-[-20px] flex translate-y-[-50px] flex-col gap-2">
          <div className="flex flex-col items-center gap-2 px-5 lg:px-8">
            {/* Profile Photo */}
            <SkeletonCircle size="size-[100px]" className="mb-1 border-[3px] border-white lg:mb-3" />

            {/* Name and Badge */}
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-2">
                <SkeletonText width="w-32" height="h-6" />
                <SkeletonCircle size="size-5" />
              </div>
              <div className="flex items-center gap-2">
                <SkeletonText width="w-24" height="h-4" />
              </div>
            </div>

            {/* Bio */}
            <div className="relative w-full items-center text-center">
              <SkeletonText width="w-full" height="h-4" className="mb-2" />
              <SkeletonText width="w-3/4" height="h-4" className="mx-auto" />
              <SkeletonText width="w-16" height="h-4" className="mx-auto mt-2" />
            </div>

            {/* Stats */}
            <div className="flex items-center gap-6">
              <div className="flex flex-col items-center gap-1">
                <SkeletonText width="w-8" height="h-5" />
                <SkeletonText width="w-12" height="h-3" />
              </div>
              <div className="flex flex-col items-center gap-1">
                <SkeletonText width="w-8" height="h-5" />
                <SkeletonText width="w-12" height="h-3" />
              </div>
              <div className="flex flex-col items-center gap-1">
                <SkeletonText width="w-8" height="h-5" />
                <SkeletonText width="w-12" height="h-3" />
              </div>
            </div>

            <div className="my-4 h-px w-full bg-[#efefef] lg:my-7" />
          </div>

          {/* General Info Sections */}
          <div className="flex flex-col gap-5 px-5 lg:gap-6 lg:px-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-start gap-3">
                <SkeletonCircle size="size-7" className="mt-1 lg:size-8" />
                <div className="flex flex-1 flex-col gap-1">
                  <SkeletonText width="w-24" height="h-4" />
                  <SkeletonText width="w-full" height="h-3" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="-mb-2 flex flex-row items-start px-5 md:mb-4">
          {['Reviews', 'Portfolio', 'Activity'].map((tab) => (
            <div key={tab} className="flex flex-1 cursor-pointer items-center justify-center pb-2.5 pt-1">
              <SkeletonText width="w-16" height="h-4" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </>
)

// Content Sections Skeleton
const ContentSectionSkeleton = () => (
  <div className="flex w-full flex-col gap-4">
    {/* Section Header */}
    <div className="flex items-center justify-between">
      <SkeletonText width="w-24" height="h-6" />
      <SkeletonText width="w-16" height="h-4" />
    </div>

    {/* Cards */}
    {[1, 2, 3].map((i) => (
      <div key={i} className="rounded-2xl border border-[#efefef] bg-white p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <SkeletonCircle size="size-[50px]" />
          <div className="flex flex-1 flex-col gap-1">
            <SkeletonText width="w-24" height="h-4" />
            <SkeletonText width="w-16" height="h-3" />
          </div>
        </div>
        <div className="mt-3 space-y-2">
          <SkeletonText width="w-full" height="h-3" />
          <SkeletonText width="w-3/4" height="h-3" />
          <SkeletonText width="w-1/2" height="h-3" />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <SkeletonCircle size="size-4" />
          <SkeletonText width="w-16" height="h-3" />
        </div>
      </div>
    ))}
  </div>
)

// Main Skeleton Component
const PublicProfileSkeleton = () => {
  const { width } = useWindowSize()
  const isMobile = width < 768

  if (!isMobile) {
    return null
  }

  return (
    <div className="flex min-h-screen w-full flex-col">
      <PublicProfileHeader />

      <main className="mx-auto mt-[70px] w-full px-0">
        <div className="flex w-full flex-col gap-4">
          <MobileHeaderSkeleton />
          <div className="w-full px-5">
            <ContentSectionSkeleton />
          </div>
        </div>
      </main>
    </div>
  )
}

export default PublicProfileSkeleton
